import { useEffect, useRef, useState, Fragment } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { callCampaignsApi } from "@/api";
import type { CallOutcome, CallCampaignStatus, CallResult } from "@/types";
import { ArrowLeft, Pause, Play, X, ChevronLeft, ChevronRight, Copy, Phone } from "lucide-react";
import { format } from "date-fns";

const GOOGLE_CHIRP_VOICES = [
  { value: "Aoede", label: "Aoede" },
  { value: "Kore", label: "Kore" },
  { value: "Leda", label: "Leda" },
  { value: "Sulafat", label: "Sulafat" },
  { value: "Charon", label: "Charon" },
  { value: "Fenrir", label: "Fenrir" },
];

const GOOGLE_GEMINI_VOICES = [
  { value: "Kore", label: "Kore" },
  { value: "Aoede", label: "Aoede" },
  { value: "Leda", label: "Leda" },
  { value: "Callirrhoe", label: "Callirrhoe" },
  { value: "Umbriel", label: "Umbriel" },
  { value: "Orus", label: "Orus" },
];

const statusColors: Record<CallCampaignStatus, string> = {
  DRAFT: "bg-slate-100 text-slate-700",
  SCHEDULED: "bg-blue-100 text-blue-700",
  RUNNING: "bg-green-100 text-green-700",
  PAUSED: "bg-yellow-100 text-yellow-700",
  COMPLETED: "bg-blue-100 text-blue-700",
  CANCELLED: "bg-red-100 text-red-700",
};

const statusLabels: Record<CallCampaignStatus, string> = {
  DRAFT: "下書き",
  SCHEDULED: "予約済",
  RUNNING: "実行中",
  PAUSED: "一時停止",
  COMPLETED: "完了",
  CANCELLED: "キャンセル",
};

const audienceLabels = {
  CONTACT: "コンタクト",
  PROSPECT: "見込みリスト",
} as const;

const outcomeLabels: Record<CallOutcome, string> = {
  PENDING: "待機中",
  CALLING: "発信中",
  CALLBACK_SCHEDULED: "コールバック",
  NOT_INTERESTED: "興味なし",
  WRONG_PERSON: "不在/違う方",
  NO_ANSWER: "不応答",
  BUSY: "話中",
  VOICEMAIL: "留守電",
  BOT_DETECTED: "AI受付",
  TRANSFER_TIMEOUT: "転送タイムアウト",
  TRANSFER_REFUSED: "転送拒否",
  OPT_OUT: "架電拒否",
  ANGRY_TERMINATION: "強い拒否",
  NEGATIVE_TERMINATION: "否定終了",
  FAILED: "失敗",
  CANCELLED: "キャンセル",
};

const outcomeColors: Record<string, string> = {
  CALLBACK_SCHEDULED: "text-green-700 bg-green-50",
  NOT_INTERESTED: "text-orange-700 bg-orange-50",
  NO_ANSWER: "text-slate-600 bg-slate-50",
  BUSY: "text-yellow-700 bg-yellow-50",
  BOT_DETECTED: "text-purple-700 bg-purple-50",
  OPT_OUT: "text-red-800 bg-red-50",
  ANGRY_TERMINATION: "text-red-800 bg-red-50",
  NEGATIVE_TERMINATION: "text-orange-800 bg-orange-50",
  FAILED: "text-red-700 bg-red-50",
  PENDING: "text-slate-500 bg-slate-50",
  CALLING: "text-blue-700 bg-blue-50",
};

const redialOutcomeOptions: Array<{ value: CallOutcome; label: string }> = [
  { value: "NO_ANSWER", label: "不応答" },
  { value: "BUSY", label: "話中" },
  { value: "VOICEMAIL", label: "留守電" },
  { value: "TRANSFER_TIMEOUT", label: "転送タイムアウト" },
  { value: "TRANSFER_REFUSED", label: "転送拒否" },
  { value: "FAILED", label: "失敗" },
  { value: "CALLBACK_SCHEDULED", label: "コールバック" },
];

const defaultRedialOutcomes: CallOutcome[] = [
  "NO_ANSWER",
  "BUSY",
  "VOICEMAIL",
  "TRANSFER_TIMEOUT",
  "TRANSFER_REFUSED",
  "FAILED",
];

const reviewStatusLabels: Record<string, string> = {
  PENDING: "未レビュー",
  PROCESSING: "処理中",
  COMPLETED: "レビュー済み",
  FAILED: "失敗",
  SKIPPED: "対象外",
};

const reviewStatusColors: Record<string, string> = {
  PENDING: "text-slate-600 bg-slate-100",
  PROCESSING: "text-blue-700 bg-blue-50",
  COMPLETED: "text-emerald-700 bg-emerald-50",
  FAILED: "text-red-700 bg-red-50",
  SKIPPED: "text-slate-500 bg-slate-50",
};

const flagLabels: Record<string, string> = {
  PREMATURE_GREETING: "先話し",
  GREETING_TRUNCATED_FOR_HUMAN: "担当者時点で途中",
  LONG_INITIAL_SILENCE: "初動無音",
  OVERLAP_AT_OPENING: "冒頭被り",
  TRANSFER_PHASE_SPEECH: "転送中開始疑い",
  RECEPTIONIST_AUDIO_CONTINUED: "受付音声継続",
  NO_CLEAR_CUSTOMER_READY_POINT: "準備点不明",
  LONG_NO_ANSWER: "長時間不応答",
  NO_ANSWER_WITH_USER_TURN: "発話あり不応答",
  WEAK_CUSTOMER_ACK: "相槌のみ",
  GENERIC_INFORMATION_REQUEST: "内容確認のみ",
  CALLBACK_WITHOUT_CLEAR_INTEREST: "CB根拠弱い",
  STT_DROPPED_OR_STALE: "STT欠落",
  BOT_DETECTED: "AI受付疑い",
  IVR_MISCLASSIFIED: "IVR誤分類疑い",
  ANSWERED_WITHOUT_USER_TURN: "発話なし応答",
  TERMINATED_ON_ACK_ONLY: "相槌終話",
};

function formatMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return "-";
  return `${ms}ms`;
}

function recognitionModeLabel(mode: string | null | undefined): string {
  switch (mode) {
    case "stt_final":
      return "STT";
    case "normalized_initial_prompt":
      return "初回正規化";
    case "assumed_initial_prompt":
      return "初回救済";
    case "assumed_short_affirmative":
      return "短文救済";
    default:
      return mode || "-";
  }
}

function renderQaCell(result: CallResult) {
  const review = result.audioReview;
  if (!review) {
    return <span className="text-xs text-slate-400">未生成</span>;
  }

  const statusClass = reviewStatusColors[review.status] || "text-slate-600 bg-slate-50";
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusClass}`}>
          {reviewStatusLabels[review.status] || review.status}
        </span>
        {typeof review.score === "number" && (
          <span className="text-xs font-semibold text-slate-700">
            {review.score}点
          </span>
        )}
      </div>
      {review.flags && review.flags.length > 0 && (
        <p className="text-xs text-slate-500">
          {flagLabels[review.flags[0]] || review.flags[0]}
          {review.flags.length > 1 ? ` 他${review.flags.length - 1}` : ""}
        </p>
      )}
    </div>
  );
}

export default function CallCampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [resultsPage, setResultsPage] = useState(1);
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [expandedTranscript, setExpandedTranscript] = useState<string | null>(null);
  const [recordingUrls, setRecordingUrls] = useState<Record<string, string>>({});
  const [recordingErrors, setRecordingErrors] = useState<Record<string, string>>({});
  const [loadingRecordingId, setLoadingRecordingId] = useState<string | null>(null);
  const [pendingAutoplayId, setPendingAutoplayId] = useState<string | null>(null);
  const [testPhone, setTestPhone] = useState("");
  const [testName, setTestName] = useState("");
  const [testCompany, setTestCompany] = useState("");
  const [testMessage, setTestMessage] = useState("");
  const [testTtsProvider, setTestTtsProvider] = useState<"openai" | "google">("openai");
  const [testTtsModel, setTestTtsModel] = useState("tts-1");
  const [testTtsVoice, setTestTtsVoice] = useState("nova");
  const [testTtsInstructions, setTestTtsInstructions] = useState("");
  const [previewAudioUrl, setPreviewAudioUrl] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [showRedialPanel, setShowRedialPanel] = useState(false);
  const [selectedRedialOutcomes, setSelectedRedialOutcomes] = useState<CallOutcome[]>(defaultRedialOutcomes);
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});

  const { data: campaign, isLoading } = useQuery({
    queryKey: ["call-campaign", id],
    queryFn: () => callCampaignsApi.get(id!),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "RUNNING" ? 2000 : false;
    },
  });

  const { data: results } = useQuery({
    queryKey: ["call-campaign-results", id, resultsPage, outcomeFilter],
    queryFn: () => {
      const params: Record<string, string> = {
        page: String(resultsPage),
        limit: "20",
      };
      if (outcomeFilter) params.outcome = outcomeFilter;
      return callCampaignsApi.results(id!, params);
    },
    enabled: !!id,
    refetchInterval: () => campaign?.status === "RUNNING" ? 2000 : false,
  });

  const { data: liveStatus } = useQuery({
    queryKey: ["call-campaign-live-status", id],
    queryFn: () => callCampaignsApi.liveStatus(id!),
    enabled: !!id && campaign?.status === "RUNNING",
    refetchInterval: () => campaign?.status === "RUNNING" ? 2000 : false,
  });

  const pauseMutation = useMutation({
    mutationFn: () => callCampaignsApi.pause(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["call-campaign", id] }),
  });

  const resumeMutation = useMutation({
    mutationFn: () => callCampaignsApi.resume(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["call-campaign", id] }),
  });

  const cancelMutation = useMutation({
    mutationFn: () => callCampaignsApi.cancel(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["call-campaign", id] }),
  });

  const testCallMutation = useMutation({
    mutationFn: () => callCampaignsApi.testCall(id!, {
      phone: testPhone.replace(/-/g, ""),
      name: testName || undefined,
      company: testCompany || undefined,
      ttsProvider: testTtsProvider,
      ttsModel: testTtsModel,
      ttsVoice: testTtsVoice,
      ttsInstructions: testTtsInstructions || undefined,
    }),
    onSuccess: () => setTestMessage("テスト発信をキュー登録しました"),
    onError: (error: Error) => setTestMessage(error.message || "テスト発信に失敗しました"),
  });

  const previewMutation = useMutation({
    mutationFn: async () => {
      const blob = await callCampaignsApi.ttsPreview(id!, {
        name: testName || undefined,
        company: testCompany || undefined,
        ttsProvider: testTtsProvider,
        ttsModel: testTtsModel,
        ttsVoice: testTtsVoice,
        ttsInstructions: testTtsInstructions || undefined,
      });
      return URL.createObjectURL(blob);
    },
    onMutate: () => {
      setPreviewError("");
      setTestMessage("");
    },
    onSuccess: (nextUrl) => {
      setPreviewAudioUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return nextUrl;
      });
    },
    onError: (error: Error) => setPreviewError(error.message || "TTSプレビューに失敗しました"),
  });

  const cloneRemainingMutation = useMutation({
    mutationFn: () => callCampaignsApi.cloneRemaining(id!),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["call-campaigns"] });
      navigate(`/call-campaigns/${created.id}/edit`);
    },
  });

  const cloneRedialMutation = useMutation({
    mutationFn: () => callCampaignsApi.cloneRedial(id!, selectedRedialOutcomes),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["call-campaigns"] });
      navigate(`/call-campaigns/${created.id}/edit`);
    },
  });

  useEffect(() => {
    const script = (campaign as any)?.callScript;
    if (!script) return;
    setTestTtsProvider(script.ttsProvider || "openai");
    setTestTtsModel(script.ttsModel || "tts-1");
    setTestTtsVoice(script.ttsVoice || "nova");
    setTestTtsInstructions(script.ttsInstructions || "");
  }, [(campaign as any)?.callScript]);

  useEffect(() => () => {
    if (previewAudioUrl) URL.revokeObjectURL(previewAudioUrl);
  }, [previewAudioUrl]);

  useEffect(() => {
    if (!pendingAutoplayId) return;
    const url = recordingUrls[pendingAutoplayId];
    const audio = audioRefs.current[pendingAutoplayId];
    if (!url || !audio) return;

    void audio.play().then(() => {
      setPendingAutoplayId((current) => (current === pendingAutoplayId ? null : current));
    }).catch(() => {
      setRecordingErrors((prev) => ({
        ...prev,
        [pendingAutoplayId]: "自動再生できませんでした。表示されたプレーヤーの再生ボタンを押してください。",
      }));
      setPendingAutoplayId((current) => (current === pendingAutoplayId ? null : current));
    });
  }, [pendingAutoplayId, recordingUrls]);

  async function handleRecordingPlay(result: CallResult) {
    if (!result.recordingKey) return;
    const existingAudio = audioRefs.current[result.id];
    if (recordingUrls[result.id] && existingAudio) {
      setPendingAutoplayId(result.id);
      return;
    }

    setLoadingRecordingId(result.id);
    setRecordingErrors((prev) => {
      const next = { ...prev };
      delete next[result.id];
      return next;
    });

    try {
      const playback = await callCampaignsApi.recordingUrl(result.id);
      // S3 presigned URLs break if extra query params are added
      const playbackUrl = playback.url.startsWith("http") ? playback.url : new URL(playback.url, window.location.origin).toString();
      setRecordingUrls((prev) => ({ ...prev, [result.id]: playbackUrl }));
      setPendingAutoplayId(result.id);
    } catch (error) {
      const message = error instanceof Error ? error.message : "録音URLの取得に失敗しました";
      setRecordingErrors((prev) => ({ ...prev, [result.id]: message }));
    } finally {
      setLoadingRecordingId((current) => (current === result.id ? null : current));
    }
  }

  async function handleInlineAudioPlay(resultId: string) {
    const audio = audioRefs.current[resultId];
    if (!audio) return;

    setRecordingErrors((prev) => {
      const next = { ...prev };
      if (next[resultId] === "自動再生できませんでした。表示されたプレーヤーの再生ボタンを押してください。") {
        delete next[resultId];
      }
      return next;
    });

    try {
      await audio.play();
    } catch {
      setRecordingErrors((prev) => ({
        ...prev,
        [resultId]: "再生を開始できませんでした。別タブで開いて確認してください。",
      }));
    }
  }

  if (isLoading || !campaign) {
    return <div className="p-6 text-slate-500">読み込み中...</div>;
  }

  const stats = campaign.resultStats || {};
  const pendingCount = stats.PENDING || 0;
  const callingCount = stats.CALLING || 0;
  const livePendingCount = liveStatus?.available ? liveStatus.pending : pendingCount;
  const liveCallingCount = liveStatus?.available ? liveStatus.calling : callingCount;
  const totalDone = Object.entries(stats)
    .filter(([k]) => k !== "PENDING" && k !== "CALLING" && k !== "CANCELLED")
    .reduce((sum, [, v]) => sum + v, 0);
  const progressPct = campaign.totalCalls > 0
    ? Math.round((totalDone / campaign.totalCalls) * 100)
    : 0;
  const selectedRedialCount = selectedRedialOutcomes.reduce((sum, outcome) => sum + (stats[outcome] || 0), 0);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button
        onClick={() => navigate("/call-campaigns")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"
      >
        <ArrowLeft size={16} /> 架電キャンペーン一覧
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">{campaign.name}</h1>
            <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[campaign.status]}`}>
              {statusLabels[campaign.status]}
            </span>
            <span className="px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700">
              {audienceLabels[campaign.audienceType]}
            </span>
          </div>
          {campaign.callScript && (
            <p className="text-sm text-slate-500 mt-1">
              商品: {(campaign as any).callScript?.productName}
            </p>
          )}
          {campaign.emailCampaign && (
            <p className="text-sm text-blue-500 mt-1">
              接続: {campaign.emailCampaign.name}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {campaign.status !== "RUNNING" && (
            <button
              onClick={() => cloneRemainingMutation.mutate()}
              className="flex items-center gap-1 px-3 py-2 border rounded-lg text-sm hover:bg-slate-50"
            >
              <Copy size={16} /> 残件からDRAFT
            </button>
          )}
          {campaign.status !== "RUNNING" && (
            <button
              onClick={() => setShowRedialPanel((current) => !current)}
              className="flex items-center gap-1 px-3 py-2 border rounded-lg text-sm hover:bg-slate-50"
            >
              <Copy size={16} /> 再架電DRAFT
            </button>
          )}
          {campaign.status === "RUNNING" && (
            <button
              onClick={() => pauseMutation.mutate()}
              className="flex items-center gap-1 px-3 py-2 border rounded-lg text-sm hover:bg-slate-50"
            >
              <Pause size={16} /> 一時停止
            </button>
          )}
          {campaign.status === "PAUSED" && (
            <button
              onClick={() => resumeMutation.mutate()}
              className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
            >
              <Play size={16} /> 再開
            </button>
          )}
          {["SCHEDULED", "RUNNING", "PAUSED"].includes(campaign.status) && (
            <button
              onClick={() => {
                if (confirm("キャンペーンをキャンセルしますか？")) cancelMutation.mutate();
              }}
              className="flex items-center gap-1 px-3 py-2 border border-red-300 text-red-600 rounded-lg text-sm hover:bg-red-50"
            >
              <X size={16} /> {campaign.status === "SCHEDULED" ? "予約キャンセル" : "キャンセル"}
            </button>
          )}
        </div>
      </div>

      {campaign.status !== "RUNNING" && showRedialPanel && (
        <div className="mb-6 rounded-lg border bg-amber-50 p-4 space-y-3">
          <div>
            <h2 className="font-semibold text-slate-900">再架電DRAFT作成</h2>
            <p className="text-xs text-slate-600">
              選んだ結果だけを新しい DRAFT に複製します。元キャンペーンや既存結果は変更しません。
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {redialOutcomeOptions.map((option) => {
              const count = stats[option.value] || 0;
              const checked = selectedRedialOutcomes.includes(option.value);
              return (
                <label
                  key={option.value}
                  className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                    count > 0 ? "border-amber-200 bg-white text-slate-700" : "border-slate-200 bg-slate-100 text-slate-400"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={count === 0}
                    onChange={(e) => {
                      setSelectedRedialOutcomes((current) => (
                        e.target.checked
                          ? Array.from(new Set([...current, option.value]))
                          : current.filter((value) => value !== option.value)
                      ));
                    }}
                  />
                  <span>{option.label}</span>
                  <span className="text-xs text-slate-500">{count}件</span>
                </label>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (selectedRedialCount === 0) return;
                if (confirm(`再架電DRAFTを作成しますか？（対象: ${selectedRedialCount}件）`)) {
                  cloneRedialMutation.mutate();
                }
              }}
              disabled={selectedRedialOutcomes.length === 0 || selectedRedialCount === 0 || cloneRedialMutation.isPending}
              className="rounded-lg bg-amber-600 px-4 py-2 text-sm text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {cloneRedialMutation.isPending ? "作成中..." : `選択結果からDRAFT作成（${selectedRedialCount}件）`}
            </button>
            <button
              onClick={() => setSelectedRedialOutcomes(defaultRedialOutcomes)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-white"
            >
              既定に戻す
            </button>
          </div>
          {cloneRedialMutation.isError && (
            <p className="text-xs text-red-600">
              {cloneRedialMutation.error instanceof Error ? cloneRedialMutation.error.message : "再架電DRAFTの作成に失敗しました"}
            </p>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg border p-4 mb-6 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-slate-900">テスト発信</h2>
            <p className="text-xs text-slate-500">実行中でも現在のキャンペーン設定で確認できます。待ち時間が長い比較用に、通話なしの音声プレビューも追加しています。</p>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-5">
          <input
            type="text"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
            placeholder="電話番号"
          />
          <input
            type="text"
            value={testCompany}
            onChange={(e) => setTestCompany(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
            placeholder="会社名（任意）"
          />
          <input
            type="text"
            value={testName}
            onChange={(e) => setTestName(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
            placeholder="担当者名（任意）"
          />
          <button
            onClick={() => {
              setTestMessage("");
              testCallMutation.mutate();
            }}
            disabled={!testPhone.trim() || testCallMutation.isPending}
            className="flex items-center justify-center gap-1 rounded-lg bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Phone size={16} />
            {testCallMutation.isPending ? "送信中..." : "テスト発信"}
          </button>
          <button
            onClick={() => previewMutation.mutate()}
            disabled={previewMutation.isPending}
            className="flex items-center justify-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Play size={16} />
            {previewMutation.isPending ? "生成中..." : "音声プレビュー"}
          </button>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">TTSプロバイダ</label>
            <select
              value={testTtsProvider}
              onChange={(e) => {
                const provider = e.target.value as "openai" | "google";
                setTestTtsProvider(provider);
                if (provider === "google") {
                  setTestTtsModel("chirp-3-hd");
                  setTestTtsVoice("Kore");
                } else {
                  setTestTtsModel("tts-1");
                  setTestTtsVoice("nova");
                }
              }}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="openai">OpenAI</option>
              <option value="google">Google Cloud</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">モデル</label>
            <select
              value={testTtsModel}
              onChange={(e) => {
                const model = e.target.value;
                setTestTtsModel(model);
                if (testTtsProvider === "google") {
                  setTestTtsVoice(model.startsWith("gemini-") ? "Kore" : "Aoede");
                } else {
                  setTestTtsVoice(model === "gpt-4o-mini-tts" ? "coral" : "nova");
                }
              }}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              {testTtsProvider === "google" ? (
                <>
                  <option value="chirp-3-hd">chirp-3-hd</option>
                  <option value="gemini-2.5-flash-tts">gemini-2.5-flash-tts</option>
                  <option value="gemini-2.5-pro-tts">gemini-2.5-pro-tts</option>
                </>
              ) : (
                <>
                  <option value="tts-1">tts-1</option>
                  <option value="tts-1-hd">tts-1-hd</option>
                  <option value="gpt-4o-mini-tts">gpt-4o-mini-tts</option>
                </>
              )}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">ボイス</label>
            <select
              value={testTtsVoice}
              onChange={(e) => setTestTtsVoice(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              {testTtsProvider === "google" ? (
                (testTtsModel.startsWith("gemini-") ? GOOGLE_GEMINI_VOICES : GOOGLE_CHIRP_VOICES).map((voice) => (
                  <option key={voice.value} value={voice.value}>{voice.label}</option>
                ))
              ) : testTtsModel === "gpt-4o-mini-tts" ? (
                <>
                  <option value="coral">coral</option>
                  <option value="alloy">alloy</option>
                  <option value="ash">ash</option>
                  <option value="sage">sage</option>
                  <option value="ballad">ballad</option>
                  <option value="verse">verse</option>
                </>
              ) : (
                <>
                  <option value="nova">nova</option>
                  <option value="alloy">alloy</option>
                  <option value="echo">echo</option>
                  <option value="fable">fable</option>
                  <option value="onyx">onyx</option>
                  <option value="shimmer">shimmer</option>
                </>
              )}
            </select>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">トーン指示（任意・テスト時のみ）</label>
          <textarea
            value={testTtsInstructions}
            onChange={(e) => setTestTtsInstructions(e.target.value)}
            rows={2}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            placeholder="例: 落ち着いた電話営業トーンで、押し売り感を抑えて短く話す"
          />
        </div>
        {testMessage && (
          <p className={`text-xs ${testCallMutation.isError ? "text-red-600" : "text-emerald-600"}`}>{testMessage}</p>
        )}
        {previewError && (
          <p className="text-xs text-red-600">{previewError}</p>
        )}
        {previewAudioUrl && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-xs text-slate-500">冒頭プレビュー: 最初の1〜2文だけを即時生成して再生します。</p>
            <audio controls src={previewAudioUrl} className="w-full" />
          </div>
        )}
        {cloneRemainingMutation.isError && (
          <p className="text-xs text-red-600">
            {cloneRemainingMutation.error instanceof Error ? cloneRemainingMutation.error.message : "残件DRAFTの作成に失敗しました"}
          </p>
        )}
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-xs text-slate-500">合計</p>
          <p className="text-2xl font-bold">{campaign.totalCalls}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-xs text-slate-500">待機中</p>
          <p className="text-2xl font-bold text-slate-700">{livePendingCount}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-xs text-slate-500">発信中</p>
          <p className="text-2xl font-bold text-indigo-600">{liveCallingCount}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-xs text-slate-500">応答</p>
          <p className="text-2xl font-bold text-blue-600">{campaign.totalAnswered}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-xs text-green-600">コールバック</p>
          <p className="text-2xl font-bold text-green-600">{campaign.totalCallbacks}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-xs text-red-500">失敗</p>
          <p className="text-2xl font-bold text-red-500">{campaign.totalFailed}</p>
        </div>
      </div>

      {/* Progress bar */}
      {campaign.totalCalls > 0 && (
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-500">進捗</span>
            <span className="font-medium">{progressPct}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-3">
            <div
              className="bg-blue-500 h-3 rounded-full transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-500">
            {Object.entries(stats).map(([outcome, count]) => (
              <span key={outcome}>
                {outcomeLabels[outcome as CallOutcome] || outcome}: {count}
              </span>
            ))}
          </div>
          {campaign.status === "RUNNING" && (
            <p className="mt-3 text-xs text-blue-600">実行中は 2 秒ごとに自動更新します</p>
          )}
        </div>
      )}

      {campaign.status === "RUNNING" && (
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-semibold text-slate-900">現在の発信状況</h2>
            <span className="text-xs text-slate-500">
              {liveStatus?.available ? `最終更新: ${format(new Date(liveStatus.refreshedAt), "MM/dd HH:mm:ss")}` : "ai-caller live 状態を取得できません"}
            </span>
          </div>
          {liveStatus?.available && liveStatus.activeCalls.length > 0 ? (
            <div className="mt-4 space-y-2">
              {liveStatus.activeCalls.map((call) => (
                <div key={call.queueId} className="flex items-center justify-between rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium text-indigo-900 truncate">
                      {call.company || "会社名なし"} {call.name ? ` / ${call.name}` : ""}
                    </p>
                    <p className="text-indigo-700">{call.phone}</p>
                  </div>
                  <div className="ml-4 shrink-0 text-right text-xs text-indigo-700">
                    <p>試行 {call.attempt}</p>
                    <p>{format(new Date(call.updatedAt), "MM/dd HH:mm:ss")}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-500">現在 `CALLING` の通話はありません。</p>
          )}
        </div>
      )}

      {/* Results table */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">通話結果</h2>
          <select
            value={outcomeFilter}
            onChange={(e) => { setOutcomeFilter(e.target.value); setResultsPage(1); }}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="">全て</option>
            <option value="CALLBACK_SCHEDULED">コールバック</option>
            <option value="NOT_INTERESTED">興味なし</option>
            <option value="NO_ANSWER">不応答</option>
            <option value="BUSY">話中</option>
            <option value="OPT_OUT">架電拒否</option>
            <option value="ANGRY_TERMINATION">強い拒否</option>
            <option value="NEGATIVE_TERMINATION">否定終了</option>
            <option value="FAILED">失敗</option>
            <option value="PENDING">待機中</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left px-4 py-2 font-medium text-slate-600">連絡先</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">会社</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">電話番号</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">結果</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">QA</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">ターン</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">通話時間</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">日時</th>
              </tr>
            </thead>
            <tbody>
              {results?.data.map((r) => (
                <Fragment key={r.id}>
                  <tr
                    className="border-b hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpandedTranscript(expandedTranscript === r.id ? null : r.id)}
                  >
                    <td className="px-4 py-2">
                      {r.contact ? (
                        <span
                          className="text-blue-600 hover:underline"
                          onClick={(e) => { e.stopPropagation(); navigate(`/contacts/${r.contactId}`); }}
                        >
                          {[r.contact.lastName, r.contact.firstName].filter(Boolean).join(" ") || r.contact.email}
                        </span>
                      ) : r.contactId.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{r.contact?.company || "-"}</td>
                    <td className="px-4 py-2 text-slate-600">{r.phone}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${outcomeColors[r.outcome] || "bg-slate-50"}`}>
                        {outcomeLabels[r.outcome] || r.outcome}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {renderQaCell(r)}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{r.turns || "-"}</td>
                    <td className="px-4 py-2 text-slate-600">
                      {r.duration > 0 ? `${r.duration}秒` : "-"}
                    </td>
                    <td className="px-4 py-2 text-slate-500">
                      {format(new Date(r.updatedAt), "MM/dd HH:mm")}
                    </td>
                  </tr>
                  {expandedTranscript === r.id && (
                    <tr key={`${r.id}-transcript`}>
                      <td colSpan={8} className="px-4 py-3 bg-slate-50">
                        <div className="space-y-4 max-w-3xl">
                          {(r.audioReview || r.trace || r.recordingKey) && (
                            <div className="rounded-lg border border-slate-200 bg-white p-3">
                              <div className="flex items-center justify-between gap-3">
                                <p className="text-xs font-medium text-slate-500">QAレビュー</p>
                                {r.audioReview && (
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${reviewStatusColors[r.audioReview.status] || "text-slate-600 bg-slate-50"}`}>
                                    {reviewStatusLabels[r.audioReview.status] || r.audioReview.status}
                                  </span>
                                )}
                              </div>
                              {r.audioReview?.summary && (
                                <p className="mt-2 text-sm text-slate-700">{r.audioReview.summary}</p>
                              )}
                              {r.audioReview?.flags && r.audioReview.flags.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {r.audioReview.flags.map((flag) => (
                                    <span key={flag} className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                                      {flagLabels[flag] || flag}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600 md:grid-cols-4">
                                <div>初動無音: {formatMs(r.audioReview?.initialSilenceMs)}</div>
                                <div>冒頭被り: {formatMs(r.audioReview?.overlapMs)}</div>
                                <div>greeting差分: {formatMs(r.audioReview?.greetingLeadMs)}</div>
                                <div className="md:col-span-1">
                                  <div className="flex items-center gap-2">
                                    <span>録音キー: {r.recordingKey || "-"}</span>
                                    {r.recordingKey && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          void handleRecordingPlay(r);
                                        }}
                                        disabled={loadingRecordingId === r.id}
                                        className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                      >
                                        <Play size={12} />
                                        {loadingRecordingId === r.id ? "取得中..." : "再生"}
                                      </button>
                                    )}
                                  </div>
                                  {recordingErrors[r.id] && (
                                    <p className="mt-1 text-xs text-red-600">{recordingErrors[r.id]}</p>
                                  )}
                                </div>
                              </div>
                              {recordingUrls[r.id] && (
                                <div className="mt-3">
                                  <div className="mb-2 flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void handleInlineAudioPlay(r.id);
                                      }}
                                      className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                    >
                                      <Play size={12} />
                                      プレーヤーで再生
                                    </button>
                                    <a
                                      href={recordingUrls[r.id]}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-xs font-medium text-blue-600 hover:text-blue-700"
                                    >
                                      別タブで開く
                                    </a>
                                  </div>
                                  <audio
                                    controls
                                    preload="none"
                                    src={recordingUrls[r.id]}
                                    ref={(node) => {
                                      audioRefs.current[r.id] = node;
                                    }}
                                    onError={() => {
                                      setRecordingErrors((prev) => ({
                                        ...prev,
                                        [r.id]: "録音の読み込みに失敗しました",
                                      }));
                                    }}
                                    className="w-full"
                                  />
                                </div>
                              )}
                              {r.trace && (
                                <div className="mt-3 space-y-2 text-xs text-slate-500">
                                  <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                                    <div>AMD解決: {formatMs(r.trace.amdResolvedAt)}</div>
                                    <div>静音確定: {formatMs(r.trace.postAmdQuietSatisfiedAt)}</div>
                                    <div>greeting開始: {formatMs(r.trace.greetingStartAt)}</div>
                                    <div>相手初回発話: {formatMs(r.trace.firstCustomerSpeechAt)}</div>
                                  </div>
                                  {(r.trace.firstCustomerResolvedText || r.trace.firstCustomerRawText || r.trace.firstCustomerRecognitionMode) && (
                                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                                      <div className="font-medium text-slate-700">初回応答監視</div>
                                      <div className="mt-1 grid grid-cols-1 gap-1 md:grid-cols-2">
                                        <div>生テキスト: {r.trace.firstCustomerRawText || "-"}</div>
                                        <div>採用テキスト: {r.trace.firstCustomerResolvedText || "-"}</div>
                                        <div>取得方法: {recognitionModeLabel(r.trace.firstCustomerRecognitionMode)}</div>
                                        <div>発話フレーム: {r.trace.firstCustomerSpeechFrames ?? "-"}</div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          <p className="text-xs font-medium text-slate-500 mb-2">トランスクリプト</p>
                          {r.transcript && r.transcript.length > 0 ? (
                            r.transcript.map((t, i) => (
                              <div key={i} className={`flex gap-2 ${t.role === "assistant" ? "" : "justify-end"}`}>
                                <div className={`px-3 py-1.5 rounded-lg text-sm max-w-md ${
                                  t.role === "assistant"
                                    ? "bg-blue-100 text-blue-900"
                                    : "bg-white border text-slate-700"
                                }`}>
                                  {t.text}
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-slate-400">トランスクリプトはありません。</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {results?.data.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                    結果がありません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {results && results.totalPages > 1 && (
          <div className="p-4 border-t flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {results.total}件中 {(resultsPage - 1) * 20 + 1}-{Math.min(resultsPage * 20, results.total)}件
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setResultsPage((p) => Math.max(1, p - 1))}
                disabled={resultsPage <= 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-slate-50"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 py-1 text-sm">
                {resultsPage} / {results.totalPages}
              </span>
              <button
                onClick={() => setResultsPage((p) => Math.min(results.totalPages, p + 1))}
                disabled={resultsPage >= results.totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-slate-50"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dates */}
      <div className="mt-4 text-xs text-slate-400 space-y-1">
        {campaign.scheduledAt && campaign.status === "SCHEDULED" && <p>予約: {format(new Date(campaign.scheduledAt), "yyyy/MM/dd HH:mm")}</p>}
        {campaign.startedAt && <p>開始: {format(new Date(campaign.startedAt), "yyyy/MM/dd HH:mm")}</p>}
        {campaign.completedAt && <p>完了: {format(new Date(campaign.completedAt), "yyyy/MM/dd HH:mm")}</p>}
        <p>作成: {format(new Date(campaign.createdAt), "yyyy/MM/dd HH:mm")}</p>
      </div>
    </div>
  );
}
